// MobilePhotoUpload.jsx — Sprint-83
// ─────────────────────────────────────────────────────────────────────
// Public, no-auth, mobile-first route that the desktop QR points at:
//   /m/upload/:sessionId
// Three steps:
//   1. Big "Take Photo" tile that opens the native camera.
//   2. Preview + Retake / Use this photo.
//   3. Upload spinner → big green ✓ "Photo sent to desktop".
// Designed for ≥320 px viewports — single column, large touch targets.
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, RotateCcw, Check, Loader2, AlertTriangle, ImageIcon, Upload as UploadIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function MobilePhotoUpload() {
  const { sessionId } = useParams();
  const fileRef = useRef(null);
  const [sessionStatus, setSessionStatus] = useState("loading"); // loading | ready | expired | notfound
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [err, setErr] = useState(null);

  // Sanity check the session exists & isn't expired
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/customer/photo-session/${sessionId}`);
        if (!alive) return;
        if (data.status === "uploaded") {
          // Someone already uploaded against this id — let user re-upload anyway
          setSessionStatus("ready");
        } else {
          setSessionStatus("ready");
        }
      } catch (e) {
        if (!alive) return;
        if (e?.response?.status === 410) setSessionStatus("expired");
        else if (e?.response?.status === 404) setSessionStatus("notfound");
        else setSessionStatus("notfound");
      }
    })();
    return () => { alive = false; };
  }, [sessionId]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      setErr("Please choose a JPEG, PNG, or WEBP image.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setErr("Image is too large. Maximum 8 MB.");
      return;
    }
    setErr(null);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const retake = () => {
    setFile(null);
    setPreview(null);
    setErr(null);
    setUploaded(false);
    // re-open the picker
    setTimeout(() => fileRef.current?.click(), 50);
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("image", file);
      await axios.post(`${API}/customer/photo-session/${sessionId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(true);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 410) setErr("This QR link has expired. Please ask the desktop to generate a new one.");
      else if (code === 413) setErr("Image is too large. Try a smaller photo.");
      else {
        // Sprint-95 — defensively flatten any error shape into a string.
        // Pydantic 422 returns an array under `detail`; the legacy
        // `detail?.message` lookup would yield the array itself and crash
        // React when rendered.
        const d = e?.response?.data?.detail;
        const msg =
          typeof d === "string"          ? d
          : Array.isArray(d)             ? d.map((x) => (typeof x?.msg === "string" ? x.msg : "")).filter(Boolean).join("; ")
          : typeof d?.message === "string" ? d.message
          : typeof d?.msg === "string"     ? d.msg
          : "Upload failed. Please try again.";
        setErr(msg);
      }
    } finally {
      setUploading(false);
    }
  };

  // ── Render ──
  if (sessionStatus === "loading") {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <Loader2 className="h-7 w-7 text-orange-500 animate-spin mb-2" />
          <p className="text-sm text-slate-500">Loading session…</p>
        </div>
      </Shell>
    );
  }
  if (sessionStatus === "expired" || sessionStatus === "notfound") {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-6" data-testid="mobile-upload-expired">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-900 mb-2">
            {sessionStatus === "expired" ? "This link has expired" : "Link not found"}
          </h2>
          <p className="text-sm text-slate-500 max-w-xs">
            Photo sessions are valid for 15 minutes. Please open a new QR code from your desktop and scan it again.
          </p>
        </div>
      </Shell>
    );
  }

  if (uploaded) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-6" data-testid="mobile-upload-success">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-5 animate-scaleIn">
            <Check className="h-10 w-10 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Photo sent!</h2>
          <p className="text-sm text-slate-500 max-w-xs">
            Your desktop has received the image. You can close this tab and continue booking.
          </p>
          <button
            onClick={retake}
            className="mt-6 text-sm font-semibold text-orange-600 underline underline-offset-2"
            data-testid="mobile-upload-retake-after-success"
          >
            Send a different photo instead
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="px-5 pt-6 pb-10 max-w-md mx-auto" data-testid="mobile-upload-page">
        <h1 className="text-xl font-bold text-slate-900 leading-tight mb-1">Take a photo of your parcel</h1>
        <p className="text-sm text-slate-500 mb-6">
          PickNGo uses this photo to confirm the size of your item and as proof at pickup.
        </p>

        {!preview && (
          <button
            onClick={() => fileRef.current?.click()}
            className="w-full bg-orange-500 text-white rounded-2xl py-8 flex flex-col items-center gap-3 active:bg-orange-600 shadow-lg shadow-orange-500/20"
            data-testid="mobile-upload-take-photo-btn"
          >
            <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center">
              <Camera className="h-9 w-9" />
            </div>
            <span className="text-base font-semibold">Take Photo</span>
            <span className="text-xs text-white/80 -mt-1">Opens your camera</span>
          </button>
        )}

        {!preview && (
          <button
            onClick={() => {
              // also allow gallery pick via a second hidden input
              const inp = document.createElement("input");
              inp.type = "file";
              inp.accept = "image/jpeg,image/png,image/webp";
              inp.onchange = onPick;
              inp.click();
            }}
            className="mt-3 w-full bg-white text-slate-700 border border-slate-200 rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2"
            data-testid="mobile-upload-gallery-btn"
          >
            <ImageIcon className="h-4 w-4" /> Choose from gallery
          </button>
        )}

        {/* Hidden picker — uses capture="environment" to prefer rear camera */}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="hidden"
          onChange={onPick}
          data-testid="mobile-upload-file-input"
        />

        {preview && (
          <div className="mt-5 space-y-4">
            <div className="rounded-2xl overflow-hidden border border-slate-200 bg-black flex items-center justify-center" data-testid="mobile-upload-preview-wrap">
              <img src={preview} alt="preview" className="max-h-[55vh] w-auto" />
            </div>
            {err && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="mobile-upload-error">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{err}</span>
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={retake}
                disabled={uploading}
                className="bg-white text-slate-700 border border-slate-200 rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="mobile-upload-retake-btn"
              >
                <RotateCcw className="h-4 w-4" /> Retake
              </button>
              <button
                onClick={submit}
                disabled={uploading}
                className="bg-emerald-500 text-white rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60 shadow-sm shadow-emerald-500/20"
                data-testid="mobile-upload-use-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadIcon className="h-4 w-4" />}
                {uploading ? "Sending…" : "Use this photo"}
              </button>
            </div>
          </div>
        )}

        {!preview && err && (
          <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="mobile-upload-pre-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{err}</span>
          </p>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <header className="bg-white border-b border-slate-200 px-5 py-3 flex items-center gap-2">
        <div className="w-7 h-7 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">P</div>
        <span className="font-bold text-slate-900">PickNGo · Photo upload</span>
      </header>
      {children}
    </div>
  );
}
